import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { addDays, endOfDay, format, startOfDay } from 'date-fns';
import { Box, FlexBox, FormDialog, Grid, Icon, Typography } from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import {
  KioskSalesByCardType,
  actions as deliveryLogActions,
  selectors as deliveryLogSelectors
} from 'store/deliveryLog';
import { exportKioskSalesReport, mergeKioskSalesByCardType } from 'utils/sfotKioskSalesReport';
import { useStyles } from '../styled';
import { SalesReportRange, SFOTSalesReportOverlayProps } from '../types';

export const SFOTSalesReportOverlay = (props: SFOTSalesReportOverlayProps) => {
  const { clientIntegration, onClose, setErrorMessage, sfotSalesReportSettings } = props;

  const classes = useStyles({ isOpen: true })();

  const { getSalesByCardTypeReport, resetSalesByCardTypeReport } = useActions({
    getSalesByCardTypeReport: deliveryLogActions.getSalesByCardTypeReport,
    resetSalesByCardTypeReport: deliveryLogActions.resetSalesByCardTypeReport
  });

  const salesByCardTypeReport = useSelector(deliveryLogSelectors.selectSalesByCardTypeReport);

  const [date, setDate] = useState<Date | null>(new Date());

  const [isBuildingReport, setIsBuildingReport] = useState(false);
  const [isExportReady, setIsExportReady] = useState(false);
  const [rangeIndex, setRangeIndex] = useState(0);
  const [environmentIndex, setEnvironmentIndex] = useState(0);
  const [mergedRangeResult, setMergedRangeResult] = useState<KioskSalesByCardType[]>([]);
  const [salesReportDayOfEvent, setSalesReportDayOfEvent] = useState(0);
  const [salesReportCurrentYearDate, setSalesReportCurrentYearDate] = useState('');
  const [salesReportPreviousYearDate, setSalesReportPreviousYearDate] = useState('');
  const [oneDayCurrentYearSalesByKiosk, setOneDayCurrentYearSalesByKiosk] = useState<
    KioskSalesByCardType[] | undefined
  >();
  const [currentYearSalesByKiosk, setCurrentYearSalesByKiosk] = useState<KioskSalesByCardType[] | undefined>();
  const [oneDayPreviousYearSalesByKiosk, setOneDayPreviousYearSalesByKiosk] = useState<
    KioskSalesByCardType[] | undefined
  >();
  const [previousYearSalesByKiosk, setPreviousYearSalesByKiosk] = useState<KioskSalesByCardType[] | undefined>();

  const isConfirmDisabled = !date;

  /**
   * The environments to fetch each range from, in fetch order: the selected
   * environment first, followed by any additional environments configured
   * for this client. When none are configured, this is just the one selected
   * environment, matching single-environment behavior.
   */
  const environmentIds = [clientIntegration.id, ...(sfotSalesReportSettings.additionalClientIntegrationIds ?? [])];

  /**
   * The 4 date ranges that make up the report, in fetch order. Rebuilt from
   * state on every render since it only depends on the report dates computed
   * once in onConfirm, so it's always in sync with the current build in progress.
   */
  const buildRanges = (currentYearDate: string, previousYearDate: string): SalesReportRange[] => [
    { startDate: currentYearDate, endDate: currentYearDate, setResult: setOneDayCurrentYearSalesByKiosk },
    { startDate: previousYearDate, endDate: previousYearDate, setResult: setOneDayPreviousYearSalesByKiosk },
    {
      startDate: sfotSalesReportSettings.currentEventStartDate!,
      endDate: currentYearDate,
      setResult: setCurrentYearSalesByKiosk
    },
    {
      startDate: sfotSalesReportSettings.previousEventStartDate!,
      endDate: previousYearDate,
      setResult: setPreviousYearSalesByKiosk
    }
  ];

  const fetchRange = (clientIntegrationId: string, range: SalesReportRange) => {
    getSalesByCardTypeReport({
      clientIntegrationId,
      startTime: convertDateStrToISOString(range.startDate, { useStartOfDay: true }),
      endTime: convertDateStrToISOString(range.endDate, { useEndOfDay: true })
    });
  };

  /**
   * This triggers actions to build the kiosk sales report for SFT.
   *
   * Each of the 4 date ranges is fetched from every environment in
   * `environmentIds` in turn, folding each new result into a running merge
   * (summing counts/amounts for kiosks that appear in more than one
   * environment) before moving on to the next environment or range.
   *
   * setTimeout() is used to ensure that the state updates are
   * completed before the next action is triggered.
   */
  useEffect(() => {
    if (!salesByCardTypeReport.data || !isBuildingReport) return;

    const ranges = buildRanges(salesReportCurrentYearDate, salesReportPreviousYearDate);
    const currentRange = ranges[rangeIndex];
    const updatedMergedResult = mergeKioskSalesByCardType(mergedRangeResult, salesByCardTypeReport.data);

    resetSalesByCardTypeReport();

    if (environmentIndex < environmentIds.length - 1) {
      const nextEnvironmentIndex = environmentIndex + 1;
      setMergedRangeResult(updatedMergedResult);
      setEnvironmentIndex(nextEnvironmentIndex);

      setTimeout(() => fetchRange(environmentIds[nextEnvironmentIndex], currentRange), 0);
      return;
    }

    currentRange.setResult(updatedMergedResult);
    setMergedRangeResult([]);
    setEnvironmentIndex(0);

    if (rangeIndex < ranges.length - 1) {
      const nextRangeIndex = rangeIndex + 1;
      setRangeIndex(nextRangeIndex);

      setTimeout(() => fetchRange(environmentIds[0], ranges[nextRangeIndex]), 0);
    } else {
      setIsBuildingReport(false);
      setIsExportReady(true);
    }
  }, [salesByCardTypeReport.data]);

  // ONCE ALL 4 RANGES HAVE BEEN FETCHED (AND MERGED, IF APPLICABLE): EXPORT
  useEffect(() => {
    if (!isExportReady) return;
    setIsExportReady(false);

    exportKioskSalesReport({
      sfotSalesReportSettings,
      salesReportCurrentYearDate,
      salesReportPreviousYearDate,
      salesReportDayOfEvent,
      oneDayCurrentYearSalesByKiosk,
      oneDayPreviousYearSalesByKiosk,
      currentYearSalesByKiosk,
      previousYearSalesByKiosk
    });

    resetSalesByCardTypeReport();
    onClose();
  }, [isExportReady]);

  // ON FETCH SALES REPORT ERROR
  useEffect(() => {
    if (salesByCardTypeReport.error) {
      setErrorMessage('Uh oh! Something went wrong. Please try again.');
      setIsBuildingReport(false);
      resetSalesByCardTypeReport();
    }
  }, [salesByCardTypeReport.error]);

  /**
   * Convert a date string (in format 'yyyy-MM-dd') to an ISO string
   * with time set to start of day or end of day based on options.
   */
  const convertDateStrToISOString = (dateStr: string, options: { useStartOfDay?: boolean; useEndOfDay?: boolean }) => {
    let result = new Date(`${dateStr}T00:00:00`);
    if (options.useStartOfDay) result = startOfDay(result);
    if (options.useEndOfDay) result = endOfDay(result);

    return result.toISOString();
  };

  const onConfirm = () => {
    if (!date) {
      setErrorMessage('Please select a date.');
      return;
    }

    if (!sfotSalesReportSettings?.currentEventStartDate || !sfotSalesReportSettings?.previousEventStartDate) {
      setErrorMessage('Sales report settings are not configured properly.');
      return;
    }

    const selectedDateISO = date.toISOString();
    const selectedDateObj = new Date(selectedDateISO);
    const currentYearReportDate = format(selectedDateObj, 'yyyy-MM-dd');
    setSalesReportCurrentYearDate(currentYearReportDate);
    const date1 = new Date(`${sfotSalesReportSettings.currentEventStartDate}T00:00:00`);
    const date2 = new Date(`${currentYearReportDate}T00:00:00`);
    const diffInDays = Math.round((date2.getTime() - date1.getTime()) / (1000 * 3600 * 24));
    const previousYearSameDay = addDays(
      new Date(`${sfotSalesReportSettings.previousEventStartDate}T00:00:00`),
      diffInDays
    );
    const prevDate = format(previousYearSameDay, 'yyyy-MM-dd');

    setSalesReportPreviousYearDate(prevDate);
    setSalesReportDayOfEvent(diffInDays + 1);
    setRangeIndex(0);
    setEnvironmentIndex(0);
    setMergedRangeResult([]);
    setIsBuildingReport(true);

    const ranges = buildRanges(currentYearReportDate, prevDate);
    fetchRange(environmentIds[0], ranges[0]);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const DatePickerCustomInput = React.forwardRef((props1: any, ref: any) => {
    return (
      <div
        onClick={props1.onClick}
        ref={ref}
        className={classes.datePickerInput}
        style={{ padding: props1.value ? '0 40px 0 12px' : '0 12px' }}
      >
        <label>{props1.value || props1.placeholder}</label>
        <Icon name="CalendarToday" sx={{ color: '#F46D2E' }} />
      </div>
    );
  });

  return (
    <FormDialog
      cancelButtonAction={onClose}
      cancelButtonText="Cancel"
      confirmButtonAction={onConfirm}
      confirmButtonDisabled={isConfirmDisabled}
      confirmButtonIsLoading={salesByCardTypeReport.pending || isBuildingReport}
      confirmButtonText="Build Report"
      open
      title="Sales Report"
    >
      <Grid direction="column" width="100%" marginTop="10px">
        <FlexBox className={classes.filtersFieldsFlexBox}>
          <Box className={classes.filtersFieldBox}>
            <Typography className={classes.filtersFieldLabel}>Select Date</Typography>
            <div className="my-datepicker-wrapper">
              <div className={classes.datePickerContainer}>
                <DatePicker
                  selected={date}
                  onChange={(d) => setDate(d)}
                  placeholderText="MM/DD/YYYY"
                  dateFormat="MM/dd/yyyy"
                  className={classes.datePickerComponent}
                  isClearable
                  customInput={<DatePickerCustomInput />}
                  popperPlacement="bottom-start"
                  popperProps={{ strategy: 'fixed' }}
                />
              </div>
            </div>
          </Box>
        </FlexBox>
      </Grid>
    </FormDialog>
  );
};
